import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Loader2, LogOut, Users, Car, Bell, Activity, RefreshCw, Save } from "lucide-react";
import { IMAGES } from "../mock";
import { adminLogin, adminLogout, adminVerify, adminStats, adminSignups, adminRides, adminPricing, adminUpdatePricing } from "../lib/adminApi";
import { formatApiError } from "../lib/api";
import { naira, StateBadge } from "../components/appHelpers";

const PRICING_FIELDS = [
  ["base_fare", "Base fare"], ["min_fare", "Minimum fare"], ["per_km", "Per km"],
  ["per_min", "Per minute"], ["waiting_per_min", "Waiting / min"], ["cancellation_fee", "Cancellation fee"],
  ["no_show_fee", "No-show fee"], ["service_fee", "Service fee"], ["airport_fee", "Airport fee"], ["max_fare", "Max fare"],
];

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0e1311] p-5">
      <div className="flex items-center gap-2 text-white/50 text-sm"><Icon className="h-4 w-4 text-[#2fd645]" /> {label}</div>
      <div className="mt-2 font-display font-extrabold text-white text-3xl">{value}</div>
    </div>
  );
}

function AdminLogin({ onAuth }) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(token);
      setToken("");
      onAuth("session");
      toast.success("Welcome, admin");
    } catch (e) { toast.error(e?.response?.status === 429 ? "Too many attempts. Try again later." : "Invalid admin token"); } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[#07090a] flex items-center justify-center px-4 id-radial">
      <form onSubmit={submit} className="w-full max-w-sm">
        <div className="flex justify-center mb-8"><img src={IMAGES.logo} alt="InstantDrive" className="h-11" /></div>
        <div className="rounded-3xl border border-white/10 bg-[#0e1311] p-7">
          <h1 className="font-display font-extrabold text-white text-xl mb-1">Admin Console</h1>
          <p className="text-white/50 text-sm mb-5">Enter your admin access token.</p>
          <div className="space-y-2">
            <Label className="text-white/80">Admin token</Label>
            <Input data-testid="admin-token-input" type="password" value={token} onChange={(e) => setToken(e.target.value)}
              className="h-12 bg-[#0a0e0c] border-white/15 text-white focus-visible:ring-[#2fd645]" placeholder="Paste token" />
          </div>
          <Button data-testid="admin-login-button" type="submit" disabled={loading} className="w-full mt-5 h-12 bg-[#2fd645] text-[#06210b] font-bold rounded-xl">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter"}
          </Button>
        </div>
        <p className="text-center text-white/40 text-sm mt-6"><a href="/" className="hover:text-[#2fd645]">← Back to website</a></p>
      </form>
    </div>
  );
}

function PricingEditor({ token }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState("");
  const load = useCallback(async () => { try { setRows(await adminPricing(token)); } catch (e) { console.error("Failed to load pricing:", e); } }, [token]);
  useEffect(() => { load(); }, [load]);

  const change = (city, key, val) => setRows((rs) => rs.map((r) => r.city === city ? { ...r, [key]: val } : r));
  const save = async (row) => {
    setSaving(row.city);
    try {
      const payload = {};
      PRICING_FIELDS.forEach(([k]) => { payload[k] = Number(row[k]); });
      await adminUpdatePricing(token, row.city, payload);
      toast.success(`${row.city} pricing saved`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setSaving(""); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-5">
      {rows.map((row) => (
        <div key={row.city} className="rounded-2xl border border-white/10 bg-[#0e1311] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-white text-lg">{row.city}</h3>
            <span className="text-xs text-white/40">{row.currency}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {PRICING_FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label className="text-white/60 text-xs">{label}</Label>
                <Input data-testid={`pricing-${row.city.toLowerCase()}-${k}`} value={row[k] ?? ""} inputMode="numeric"
                  onChange={(e) => change(row.city, k, e.target.value.replace(/[^\d.]/g, ""))}
                  className="h-10 bg-[#0a0e0c] border-white/15 text-white focus-visible:ring-[#2fd645]" />
              </div>
            ))}
          </div>
          <Button data-testid={`pricing-save-${row.city.toLowerCase()}`} onClick={() => save(row)} disabled={saving === row.city} className="mt-4 h-10 px-4 bg-[#2fd645] text-[#06210b] font-bold rounded-lg">
            {saving === row.city ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save {row.city}</>}
          </Button>
        </div>
      ))}
    </div>
  );
}

function SignupsTable({ token }) {
  const [kind, setKind] = useState("");
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => { try { setRows(await adminSignups(token, kind)); } catch (e) { console.error("Failed to load signups:", e); } }, [token, kind]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {[["", "All"], ["waitlist", "Waitlist"], ["rider", "Riders"], ["driver", "Drivers"]].map(([v, l]) => (
          <button key={l} data-testid={`signups-filter-${l.toLowerCase()}`} onClick={() => setKind(v)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${kind === v ? "bg-[#2fd645] text-[#06210b]" : "bg-[#0e1311] text-white/60 border border-white/10"}`}>{l}</button>
        ))}
        <button data-testid="signups-refresh" onClick={load} className="ml-auto px-3 py-1.5 rounded-lg text-sm text-white/60 border border-white/10 flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-[#0b0f0d] text-white/50"><tr>
            {["Type", "Name", "Email", "Phone", "WhatsApp", "City", "NIN", "Vehicle", "When"].map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-white/40">No records.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-white/5 text-white/80">
                <td className="px-4 py-3 capitalize">{r.kind}</td>
                <td className="px-4 py-3">{r.name}</td>
                <td className="px-4 py-3">{r.email || "—"}</td>
                <td className="px-4 py-3">{r.phone || "—"}</td>
                <td className="px-4 py-3">{r.whatsapp || "—"}</td>
                <td className="px-4 py-3">{r.city || "—"}</td>
                <td className="px-4 py-3">{r.nin || "—"}</td>
                <td className="px-4 py-3">{r.vehicle || "—"}</td>
                <td className="px-4 py-3 text-white/40">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RidesMonitor({ token }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => { try { setRows(await adminRides(token)); } catch (e) { console.error("Failed to load rides:", e); } }, [token]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button data-testid="rides-refresh" onClick={load} className="px-3 py-1.5 rounded-lg text-sm text-white/60 border border-white/10 flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button></div>
      {rows.length === 0 && <p className="text-white/40">No rides yet.</p>}
      {rows.map((r) => (
        <div key={r.id} className="rounded-2xl border border-white/10 bg-[#0e1311] p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm text-white/80">{r.pickup} → {r.destination} <span className="text-white/40">· {r.city} · {r.category}</span></div>
            <div className="text-xs text-white/40 mt-1">Passenger: {r.passenger_name}{r.driver_name ? ` · Driver: ${r.driver_name}` : ""} · {r.distance_km} km</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-bold text-[#2fd645]">{naira(r.final_fare || r.proposed_fare)}</span>
            <StateBadge state={r.state} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState("session");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      localStorage.removeItem("id_admin_token");
      try { await adminVerify(); setAuthed(true); } catch { setAuthed(false); }
      setChecking(false);
    })();
  }, [token]);

  const loadStats = useCallback(async () => { if (authed) { try { setStats(await adminStats(token)); } catch (e) { console.error("Failed to load stats:", e); } } }, [authed, token]);
  useEffect(() => { loadStats(); const t = setInterval(loadStats, 8000); return () => clearInterval(t); }, [loadStats]);

  const logout = async () => { try { await adminLogout(); } finally { setAuthed(false); } };

  if (checking) return <div className="min-h-screen bg-[#07090a] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#2fd645]" /></div>;
  if (!authed) return (<AdminLogin onAuth={(t) => { setToken(t); setAuthed(true); }} />);

  return (
    <div className="min-h-screen bg-[#07090a]">
      <header className="sticky top-0 z-40 bg-[#07090a]/90 backdrop-blur-md border-b border-white/10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><img src={IMAGES.logo} alt="InstantDrive" className="h-9" /><span className="text-white/50 text-sm font-semibold">Admin</span></div>
          <button data-testid="admin-logout-button" aria-label="Log out" onClick={logout} className="h-9 w-9 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/70 hover:text-[#2fd645]"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Bell} label="Waitlist" value={stats.waitlist} />
            <StatCard icon={Users} label="Riders" value={stats.riders} />
            <StatCard icon={Car} label="Drivers" value={stats.drivers} />
            <StatCard icon={Activity} label="Active rides" value={stats.rides_active} />
          </div>
        )}

        <Tabs defaultValue="signups">
          <TabsList className="bg-[#0e1311] border border-white/10 rounded-xl p-1 mb-6">
            <TabsTrigger data-testid="admin-tab-signups" value="signups" className="rounded-lg data-[state=active]:bg-[#2fd645] data-[state=active]:text-[#06210b] text-white/60">Signups</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-rides" value="rides" className="rounded-lg data-[state=active]:bg-[#2fd645] data-[state=active]:text-[#06210b] text-white/60">Live rides</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-pricing" value="pricing" className="rounded-lg data-[state=active]:bg-[#2fd645] data-[state=active]:text-[#06210b] text-white/60">Pricing</TabsTrigger>
          </TabsList>
          <TabsContent value="signups"><SignupsTable token={token} /></TabsContent>
          <TabsContent value="rides"><RidesMonitor token={token} /></TabsContent>
          <TabsContent value="pricing"><PricingEditor token={token} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
